import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { useAuth } from './AuthContext';
import { usePreferences } from './PreferencesContext';
import { THEMES as themes } from '../theme/themes';
import {
  migrateThemeKeysOnce,
  DEFAULT_THEME_ID,
  isValidTheme,
  getStoredTheme,
  setStoredTheme,
  THEME_STORAGE_KEY
} from '../theme';

// Theme registry moved to src/theme/themes.js (was lines 6-240)

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const { user, userId } = useAuth();
  const { preferences: sharedPrefs, loading: prefsLoading, patchPreferences } = usePreferences();
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Run unified migration pipeline to handle all legacy keys
    const { themeId } = migrateThemeKeysOnce();
    return themeId ?? DEFAULT_THEME_ID;
  });
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const fetchedServerPrefRef = useRef(false); // prevent double fetch
  const isInitialMountRef = useRef(true); // prevent server sync on initial hydration

  // Resolve effective user id (works even before full user object is loaded)
  const preferenceUserId = React.useMemo(() => {
    return (user?.id || user?.userId || userId || localStorage.getItem('user_id')) || null;
  }, [user, userId]);

  // Server UI Preferences bootstrap — reads from shared PreferencesContext (single fetch)
  useEffect(() => {
    // Enforce localStorage-first: never override local theme selection
    const localTheme = getStoredTheme();
    if (localTheme && isValidTheme(localTheme)) {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== 'production') console.log('[Theme] Bootstrap skipped: local theme takes precedence', localTheme);
      return;
    }

    // Wait for PreferencesContext to finish loading
    if (prefsLoading || !sharedPrefs || fetchedServerPrefRef.current) return;
    fetchedServerPrefRef.current = true;

    // sharedPrefs is already the migrated blob from PreferencesContext
    // (schema migration now runs centrally in PreferencesContext)

    // eslint-disable-next-line no-console
    console.debug('[Theme] Loaded UI prefs (from PreferencesContext):', sharedPrefs);
    if (sharedPrefs && typeof sharedPrefs === 'object') {
      const serverTheme = sharedPrefs.theme;
      if (serverTheme && themes[serverTheme]) {
        if (serverTheme !== currentTheme) {
          setCurrentTheme(serverTheme);
        }
      } else if (!serverTheme) {
        // Seed default theme through shared context
        patchPreferences({ theme: DEFAULT_THEME_ID });
        if (currentTheme !== DEFAULT_THEME_ID) setCurrentTheme(DEFAULT_THEME_ID);
      }
    } else {
      patchPreferences({ theme: DEFAULT_THEME_ID });
      if (currentTheme !== DEFAULT_THEME_ID) setCurrentTheme(DEFAULT_THEME_ID);
    }
  }, [sharedPrefs, prefsLoading, currentTheme, patchPreferences]);
  
  // Persist locally & debounced remote save
  useEffect(() => {
    // Always persist to localStorage immediately
    setStoredTheme(currentTheme);
    
    // Skip server sync on initial mount (only sync on explicit user changes)
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    
    if (preferenceUserId) {
      // Use shared PreferencesContext for debounced persistence
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== 'production') console.log('[Theme] Persisting theme via patchPreferences', { theme: currentTheme });
      patchPreferences({ theme: currentTheme });
    }
  }, [currentTheme, preferenceUserId, patchPreferences]);
  
  // Cross-tab synchronization
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key !== THEME_STORAGE_KEY) return;
      if (!e.newValue) return;
      if (isValidTheme(e.newValue)) {
        setCurrentTheme(e.newValue);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Apply theme to document
  useEffect(() => {
    const theme = themes[currentTheme];
    if (!theme) return;

    // Attach a theme-* class to <html> for any CSS rules (like button normalizations)
    // that rely on a parent theme scope. Remove any previous theme-* classes first.
    const rootEl = document.documentElement;
    try {
      Array.from(rootEl.classList)
        .filter(c => c.startsWith('theme-'))
        .forEach(c => rootEl.classList.remove(c));
      rootEl.classList.add(`theme-${currentTheme}`);
    } catch (e) {
      // non-fatal; continue applying inline CSS variables
      // eslint-disable-next-line no-console
      console.warn('[Theme] Failed to toggle theme class', e);
    }
    
    // Apply theme CSS variables
    document.documentElement.style.setProperty('--background-color', theme.colors.background);
    document.documentElement.style.setProperty('--text-color', theme.colors.text);
    document.documentElement.style.setProperty('--primary-color', theme.colors.primary);
    document.documentElement.style.setProperty('--accent-color', theme.colors.accent);
    document.documentElement.style.setProperty('--card-bg-color', theme.colors.cardBg);
    document.documentElement.style.setProperty('--button-bg-color', theme.colors.buttonBg);
    document.documentElement.style.setProperty('--button-text-color', theme.colors.buttonText);
    document.documentElement.style.setProperty('--body-font', theme.fonts.body);
    document.documentElement.style.setProperty('--heading-font', theme.fonts.heading);
    document.documentElement.style.setProperty('--border-radius', theme.borderRadius);
    
    // Emit semantic surface + text + accent tokens derived from theme palette
    // Surfaces map: background -> page, cardBg -> card, and intermediates blended or reused
    document.documentElement.style.setProperty('--semantic-surface-page', theme.colors.background);
    document.documentElement.style.setProperty('--semantic-surface-section', theme.colors.background);
    document.documentElement.style.setProperty('--semantic-surface-panel', theme.colors.cardBg);
    document.documentElement.style.setProperty('--semantic-surface-card', theme.colors.cardBg);
  // Additional surface aliases consumed by layout (sidebar background gradient stops)
  document.documentElement.style.setProperty('--semantic-surface-app', theme.colors.background);
  document.documentElement.style.setProperty('--semantic-surface-sidebar-panel', theme.colors.cardBg);

    // Accent / brand mapping (primary as brand, accent as accent border/text cues)
    document.documentElement.style.setProperty('--color-brand-primary', theme.colors.primary);
    document.documentElement.style.setProperty('--color-brand-primary-hover', theme.colors.accent);
    document.documentElement.style.setProperty('--color-brand-accent', theme.colors.accent);

    // -------------------------------------------------------------
    // Checkbox Tokens (theme-reactive - use solid primary color for checked state)
    // -------------------------------------------------------------
    // Use the actual theme primary color for checkbox checked state (not action-primary-bg which can be white)
    const checkboxCheckedBg = theme.colors.primary; // Use solid primary color
    const checkboxCheckedBorder = theme.colors.accent || theme.colors.primary;
    const checkboxCheckColor = '#ffffff'; // White checkmark by default (works on colored backgrounds)
    
    document.documentElement.style.setProperty('--checkbox-checked-bg', checkboxCheckedBg);
    document.documentElement.style.setProperty('--checkbox-checked-border', checkboxCheckedBorder);
    document.documentElement.style.setProperty('--checkbox-check-color', checkboxCheckColor);

    // -------------------------------------------------------------
    // Unified Button / Action Tokens (theme-reactive)
    // -------------------------------------------------------------
    const primaryBase = theme.colors.buttonBg || theme.colors.primary;
  const unifiedPrimaryText = theme.colors.buttonText || '#ffffff';
    // Utility: adjust color (lighten/darken) by % of 255 per channel
    const adjust = (hex, pct) => {
      if (!hex || typeof hex !== 'string') return hex;
      let h = hex.replace('#','');
      if (h.length === 3) h = h.split('').map(c=>c+c).join('');
      if (h.length !== 6) return hex;
      const num = parseInt(h,16);
      let r=(num>>16)&255, g=(num>>8)&255, b=num&255;
      const delta = Math.round(255 * pct);
      const clamp = v => Math.min(255, Math.max(0, v));
      r = clamp(r + delta); g = clamp(g + delta); b = clamp(b + delta);
      return '#'+((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1);
    };
    const isLight = (hex) => {
      if (!hex) return false;
      let h = hex.replace('#','');
      if (h.length === 3) h = h.split('').map(c=>c+c).join('');
      if (h.length !== 6) return false;
      const num = parseInt(h,16);
      const r=(num>>16)&255, g=(num>>8)&255, b=num&255;
      const L = (0.299*r + 0.587*g + 0.114*b)/255;
      return L > 0.58;
    };
    
  // Hover and muted surface variations (derived from card background for consistent theming)
  // IMPORTANT: These must always resolve to SOLID (opaque) hex colours so that
  // components swapping their background on :hover never become transparent and
  // accidentally reveal a differently-tinted parent layer.
  const surfaceHover = adjust(theme.colors.cardBg, isLight(theme.colors.cardBg) ? -0.03 : 0.08);
  const surfaceMuted = adjust(theme.colors.cardBg, isLight(theme.colors.cardBg) ? -0.05 : 0.10);
  document.documentElement.style.setProperty('--semantic-surface-hover', surfaceHover);
  document.documentElement.style.setProperty('--semantic-surface-muted', surfaceMuted);
  // --table-row-hover: unified alias so list/card/row hovers all resolve to the
  // same solid neutral colour.  Prevents the recurring "gold hover" issue where
  // components fell back to a transparent rgba() value.
  document.documentElement.style.setProperty('--table-row-hover', surfaceHover);

  // Overlay surface: slightly elevated from card background
  const surfaceOverlay = adjust(theme.colors.cardBg, isLight(theme.colors.cardBg) ? -0.02 : 0.04);
  document.documentElement.style.setProperty('--semantic-surface-overlay', surfaceOverlay);

  // Accent-primary tokens: used by quiz choices, buttons, active states
  const accentPrimary = theme.colors.primary;
  const accentPrimaryHover = adjust(accentPrimary, isLight(accentPrimary) ? -0.10 : 0.12);
  document.documentElement.style.setProperty('--semantic-accent-primary', accentPrimary);
  document.documentElement.style.setProperty('--semantic-accent-primary-hover', accentPrimaryHover);
  // Alpha variant for focus rings / subtle glows
  {
    let h = accentPrimary.replace('#','');
    if (h.length === 3) h = h.split('').map(c=>c+c).join('');
    const n = parseInt(h,16);
    const ar=(n>>16)&255, ag=(n>>8)&255, ab=n&255;
    document.documentElement.style.setProperty('--semantic-accent-primary-alpha', `rgba(${ar},${ag},${ab},0.25)`);
    document.documentElement.style.setProperty('--semantic-surface-accent-subtle', `rgba(${ar},${ag},${ab},0.08)`);
  }

  // Border tokens derived from theme (ensures quiz choices, cards, etc. use theme-aware borders)
  const cardBgIsLight = isLight(theme.colors.cardBg);
  const borderDefault = cardBgIsLight ? '#d0d7de' : '#2d3a44';
  const borderHover = cardBgIsLight
    ? adjust(theme.colors.cardBg, -0.12)
    : adjust(theme.colors.cardBg, 0.15);
  document.documentElement.style.setProperty('--semantic-border-default', borderDefault);
  document.documentElement.style.setProperty('--semantic-border-hover', borderHover);

  // Elevated surface (slightly above card for question cards, modals, etc.)
  const surfaceElevated = adjust(theme.colors.cardBg, cardBgIsLight ? 0.02 : 0.05);
  document.documentElement.style.setProperty('--semantic-surface-elevated', surfaceElevated);

  // -------------------------------------------------------------
  // Dynamic Shadow Tokens (theme-reactive — heavier in dark, softer in light)
  // -------------------------------------------------------------
  const shadowAlpha = cardBgIsLight ? '0.08' : '0.35';
  const shadowAlphaLg = cardBgIsLight ? '0.12' : '0.45';
  document.documentElement.style.setProperty('--shadow-card',
    `0 2px 8px rgba(0,0,0,${shadowAlpha})`);
  document.documentElement.style.setProperty('--shadow-card-hover',
    `0 8px 24px rgba(0,0,0,${shadowAlphaLg})`);
  document.documentElement.style.setProperty('--shadow-elevated',
    `0 12px 32px rgba(0,0,0,${shadowAlphaLg}), 0 2px 6px rgba(0,0,0,${shadowAlpha})`);
  // Brand glow (used on selected / active cards)
  {
    let h = accentPrimary.replace('#','');
    if (h.length === 3) h = h.split('').map(c=>c+c).join('');
    const n = parseInt(h,16);
    const pr=(n>>16)&255, pg=(n>>8)&255, pb=n&255;
    document.documentElement.style.setProperty('--shadow-brand',
      `0 4px 14px rgba(${pr},${pg},${pb},0.35), 0 0 0 2px rgba(${pr},${pg},${pb},0.18)`);
  }

  // Subtle surface for neutral interactive controls (filters, toggles)
  const surfaceSubtle = adjust(theme.colors.background, cardBgIsLight ? -0.02 : 0.04);
  document.documentElement.style.setProperty('--semantic-surface-subtle', surfaceSubtle);

  // Accent border (thin brand-colored accent line for section cards)
  document.documentElement.style.setProperty('--semantic-border-accent', accentPrimary);

  // Status / intent semantic tokens used by quizzes
  const successColor = '#16a34a';
  const warningColor = '#f59e0b';
  document.documentElement.style.setProperty('--semantic-success', successColor);
  document.documentElement.style.setProperty('--semantic-success-subtle', cardBgIsLight ? 'rgba(22,163,74,0.08)' : 'rgba(22,163,74,0.12)');
  document.documentElement.style.setProperty('--semantic-warning', warningColor);
  document.documentElement.style.setProperty('--semantic-warning-subtle', cardBgIsLight ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.12)');
  document.documentElement.style.setProperty('--semantic-primary', accentPrimary);
  document.documentElement.style.setProperty('--semantic-primary-subtle', `rgba(${(() => { let h2 = accentPrimary.replace('#',''); if(h2.length===3) h2=h2.split('').map(c2=>c2+c2).join(''); const n2=parseInt(h2,16); return `${(n2>>16)&255},${(n2>>8)&255},${n2&255}`; })()},${cardBgIsLight ? '0.08' : '0.15'})`);

  // Quiz-specific: text color that always contrasts with surface-hover (prevents text blending on hover)
  // For dark themes, use a bright neutral instead of the theme text (which can be tinted/gold)
  const quizChoiceText = cardBgIsLight ? theme.colors.text : '#e8eaed';
  document.documentElement.style.setProperty('--semantic-quiz-choice-text', quizChoiceText);

  const primaryHover = adjust(primaryBase, isLight(primaryBase) ? -0.12 : 0.14);
    const secondaryBg = theme.colors.cardBg || '#f5f5f5';
    const secondaryHover = adjust(secondaryBg, isLight(secondaryBg) ? -0.08 : 0.12);
    const secondaryText = theme.colors.text;
    const neutralBg = secondaryBg;
  const unifiedNeutralHover = secondaryHover;
    const neutralText = theme.colors.text;
    const accentBase = theme.colors.accent || primaryBase;
    const accentHover = adjust(accentBase, isLight(accentBase) ? -0.14 : 0.16);
    const accentText = isLight(accentBase) ? '#1a1c20' : '#ffffff';
    const dangerBase = '#dc2626';
    const dangerHover = '#b91c1c';
    const dangerText = '#ffffff';
    const setVar = (k,v)=>document.documentElement.style.setProperty(k,v);
    const isPrimaryTheme = currentTheme === 'primaryTheme';
    const solidPrimaryBg = primaryBase;
    const solidPrimaryHover = primaryHover;
    // Action (generic) tokens map to solid brand style for all themes.
    // primaryTheme previously used white surface but that made buttons invisible on white pages.
    setVar('--action-primary-bg', solidPrimaryBg);
    setVar('--action-primary-bg-hover', solidPrimaryHover);
    setVar('--action-primary-text', unifiedPrimaryText);
    setVar('--action-secondary-bg', secondaryBg);
    setVar('--action-secondary-bg-hover', secondaryHover);
    setVar('--action-secondary-text', secondaryText);
    setVar('--button-neutral-bg', neutralBg);
  setVar('--button-neutral-bg-hover', unifiedNeutralHover);
  setVar('--button-neutral-fg', neutralText);
    setVar('--action-accent-bg', accentBase);
    setVar('--action-accent-bg-hover', accentHover);
  setVar('--action-accent-text', accentText);
    setVar('--action-danger-bg', dangerBase);
    setVar('--action-danger-bg-hover', dangerHover);
  setVar('--action-danger-text', dangerText);
  setVar('--button-radius', theme.borderRadius || '8px');

    // Circular icon buttons — derive from theme for all themes
    const circularBtnBg = adjust(theme.colors.cardBg || '#ffffff', isLight(theme.colors.cardBg || '#ffffff') ? -0.06 : 0.08);
    setVar('--semantic-button-icon-button-circular-bg', circularBtnBg);
    setVar('--semantic-button-icon-button-circular-border', primaryBase);
    setVar('--semantic-button-icon-button-circular-icon', theme.colors.accent || primaryBase);
    setVar('--semantic-button-icon-button-circular-bg-hover', primaryBase);
    setVar('--semantic-button-icon-button-circular-border-hover', primaryHover);
    setVar('--semantic-button-icon-button-circular-icon-hover', unifiedPrimaryText);

    // Primary theme explicit additive tokens for add button + neutral baseline
    if (isPrimaryTheme) {
      setVar('--button-add-bg', '#ffffff');
      setVar('--button-add-bg-hover', '#f2f4f7');
      setVar('--button-add-bg-active', '#e4e7eb');
      setVar('--button-add-fg', theme.colors.text);
      setVar('--button-add-border', '#d0d7de');
    }

  // Basic border alignment (derive subtle/strong by luminosity heuristics)
  // Dynamically detect dark themes instead of maintaining a static allowlist so that
  // any newly added theme with a dark background automatically receives dark tokens.
  // Explicitly define dark themes - single source of truth
  const isDarkTheme = ['darkLab', 'retroVibes', 'docuHounds', 'midnightGold', 'velvetNight', 'steelCore', 'neonCircuit', 'crimsonFocus'].includes(currentTheme);

  // Bridge the JS-driven theme system with the token CSS system.
  // tokens-dark.css keys off [data-theme='dark']; tokens-primary.css keys off [data-brand='primary'].
  try {
    const rootEl = document.documentElement;
    rootEl.dataset.theme = isDarkTheme ? 'dark' : 'light';
    if (currentTheme === 'primaryTheme') rootEl.dataset.brand = 'primary';
    else delete rootEl.dataset.brand;
  } catch (e) {}

  document.documentElement.style.setProperty('--color-border-default', isDarkTheme ? '#2d3a44' : '#d0d7de');
  document.documentElement.style.setProperty('--color-border-strong', isDarkTheme ? '#3a4a55' : '#b6bec6');
  document.documentElement.style.setProperty('--color-border-contrast', isDarkTheme ? '#4c5d68' : '#94a3b8');

    // Text tokens
    document.documentElement.style.setProperty('--color-text-primary', theme.colors.text);
    document.documentElement.style.setProperty('--semantic-text-primary', theme.colors.text);
    // Derive secondary/muted by simple opacity layering fallback if not provided
    document.documentElement.style.setProperty('--color-text-secondary', 'rgba(0,0,0,0.72)');
    document.documentElement.style.setProperty('--semantic-text-secondary', 'rgba(0,0,0,0.72)');
    document.documentElement.style.setProperty('--color-text-muted', 'rgba(0,0,0,0.55)');
    document.documentElement.style.setProperty('--semantic-text-muted', 'rgba(0,0,0,0.55)');
    if (['darkLab','retroVibes','midnightGold','velvetNight','steelCore','neonCircuit','crimsonFocus'].includes(currentTheme)) {
      document.documentElement.style.setProperty('--color-text-secondary', 'rgba(255,255,255,0.75)');
      document.documentElement.style.setProperty('--semantic-text-secondary', 'rgba(255,255,255,0.75)');
      document.documentElement.style.setProperty('--color-text-muted', 'rgba(255,255,255,0.55)');
      document.documentElement.style.setProperty('--semantic-text-muted', 'rgba(255,255,255,0.55)');
    }
    if (currentTheme === 'docuHounds') {
      // Gold hierarchy for dark DocuHounds theme
      document.documentElement.style.setProperty('--color-text-secondary', '#e6bb7d');
      document.documentElement.style.setProperty('--semantic-text-secondary', '#e6bb7d');
      document.documentElement.style.setProperty('--color-text-muted', '#cfa66a');
      document.documentElement.style.setProperty('--semantic-text-muted', '#cfa66a');
      
      // Gold primary action tokens
      document.documentElement.style.setProperty('--color-action-primary', '#f2c586');
      document.documentElement.style.setProperty('--color-action-primary-hover', '#e6bb7d');
      document.documentElement.style.setProperty('--color-action-primary-text', '#0d0f13');
      
      // Fix brand hover to stay blue (not gold)
      document.documentElement.style.setProperty('--color-brand-primary-hover', '#4a8fd6');
      
      // Reduce sidebar hover emphasis for calmer feel
      document.documentElement.style.setProperty('--semantic-sidebar-link-hover-bg', 'rgba(255,255,255,0.06)');

      // Explicitly neutral hover surfaces for DocuHounds so no gold tint leaks
      // into card/row hover states.  Uses a cool-gray that contrasts slightly
      // with cardBg (#161b22) without any warm/amber cast.
      document.documentElement.style.setProperty('--semantic-surface-hover', '#1e2530');
      document.documentElement.style.setProperty('--table-row-hover', '#1e2530');
      document.documentElement.style.setProperty('--table-row-hover-bg', '#1e2530');

      // Circular icon buttons: gold border/icon on dark surface
      document.documentElement.style.setProperty('--semantic-button-icon-button-circular-bg', '#161b22');
      document.documentElement.style.setProperty('--semantic-button-icon-button-circular-border', '#f2c586');
      document.documentElement.style.setProperty('--semantic-button-icon-button-circular-icon', '#f2c586');
      document.documentElement.style.setProperty('--semantic-button-icon-button-circular-bg-hover', '#f2c586');
      document.documentElement.style.setProperty('--semantic-button-icon-button-circular-border-hover', '#e6bb7d');
      document.documentElement.style.setProperty('--semantic-button-icon-button-circular-icon-hover', '#0d0f13');
    }
    document.documentElement.style.setProperty('--color-text-inverse', '#ffffff');

  // Establish subtle border token then input + tabs baseline
  const subtleBorder = isDarkTheme ? '#37424c' : '#d9e1e7';
  document.documentElement.style.setProperty('--border-subtle', subtleBorder);
  document.documentElement.style.setProperty('--semantic-border-subtle', subtleBorder);
  
  // For dark themes, make section surface slightly lighter than background for better gradient visibility
  if (isDarkTheme) {
    const sectionBg = adjust(theme.colors.background, 0.02);
    document.documentElement.style.setProperty('--semantic-surface-section', sectionBg);
  }
  // In darker themes prefer panel tone for inputs; light themes keep card
    // Dark theme input surface a bit lighter than panel to distinguish from panel background, with subtle elevation
    document.documentElement.style.setProperty('--semantic-input-bg', isDarkTheme ? '#222c35' : 'var(--semantic-surface-card)');
    if (isDarkTheme) {
      document.documentElement.style.setProperty('--search-placeholder', 'rgba(255,255,255,0.35)');
    }
  document.documentElement.style.setProperty('--semantic-input-border', 'var(--border-subtle)');
    document.documentElement.style.setProperty('--semantic-input-text', 'var(--color-text-primary)');
    document.documentElement.style.setProperty('--semantic-tabs-bg', 'var(--semantic-surface-panel)');
    document.documentElement.style.setProperty('--semantic-tabs-bg-active', 'var(--semantic-surface-card)');
    document.documentElement.style.setProperty('--semantic-tabs-text', 'var(--color-text-secondary)');
    document.documentElement.style.setProperty('--semantic-tabs-text-active', 'var(--color-text-primary)');
    document.documentElement.style.setProperty('--semantic-tabs-indicator', 'var(--color-brand-primary)');

    // Focus ring color alignment
    document.documentElement.style.setProperty('--action-focus-ring', 'var(--color-brand-accent)');

    // Navigation semantic tokens (derive from palette; dark themes get translucent surfaces)
    if (isDarkTheme) {
      document.documentElement.style.setProperty('--semantic-nav-bg', 'linear-gradient(90deg, rgba(15,23,42,0.9), rgba(30,41,59,0.92))');
      document.documentElement.style.setProperty('--semantic-nav-bg-scrolled', 'rgba(15,23,42,0.85)');
      document.documentElement.style.setProperty('--semantic-nav-border', 'rgba(255,255,255,0.10)');
      document.documentElement.style.setProperty('--semantic-nav-divider', 'rgba(255,255,255,0.1)');
      document.documentElement.style.setProperty('--semantic-nav-link-default', 'var(--text-secondary)');
      document.documentElement.style.setProperty('--semantic-nav-link-hover', 'var(--text-primary)');
      document.documentElement.style.setProperty('--semantic-nav-link-hover-bg', 'rgba(255,255,255,0.08)');
      document.documentElement.style.setProperty('--semantic-nav-link-active', 'var(--text-primary)');
      // Sidebar specific semantic tokens (parallel to nav, can diverge later)
      document.documentElement.style.setProperty('--semantic-sidebar-bg', 'linear-gradient(185deg, rgba(30,41,59,0.94) 0%, rgba(15,23,42,0.92) 100%)');
      document.documentElement.style.setProperty('--semantic-sidebar-border', 'rgba(255,255,255,0.10)');
      document.documentElement.style.setProperty('--semantic-sidebar-link-default', 'var(--text-muted)');
      document.documentElement.style.setProperty('--semantic-sidebar-link-hover', 'var(--text-inverse)');
      document.documentElement.style.setProperty('--semantic-sidebar-link-active', 'var(--text-inverse)');
      // Reduced emphasis for DocuHounds specifically (if needed, override in DocuHounds block)
      document.documentElement.style.setProperty('--semantic-sidebar-link-hover-bg', 'rgba(255,255,255,0.08)');
    } else {
      document.documentElement.style.setProperty('--semantic-nav-bg', 'linear-gradient(90deg, '+theme.colors.primary+', '+theme.colors.accent+')');
      document.documentElement.style.setProperty('--semantic-nav-bg-scrolled', theme.colors.primary);
      document.documentElement.style.setProperty('--semantic-nav-border', 'rgba(0,0,0,0.07)');
      document.documentElement.style.setProperty('--semantic-nav-divider', 'rgba(255,255,255,0.15)');
      document.documentElement.style.setProperty('--semantic-nav-link-default', 'var(--text-inverse)');
      document.documentElement.style.setProperty('--semantic-nav-link-hover', '#ffffff');
      document.documentElement.style.setProperty('--semantic-nav-link-hover-bg', 'rgba(255,255,255,0.10)');
      document.documentElement.style.setProperty('--semantic-nav-link-active', '#ffffff');
      document.documentElement.style.setProperty('--semantic-sidebar-bg', 'linear-gradient(185deg, var(--semantic-surface-card) 0%, var(--semantic-surface-page) 100%)');
      document.documentElement.style.setProperty('--semantic-sidebar-border', 'rgba(0,0,0,0.08)');
      document.documentElement.style.setProperty('--semantic-sidebar-link-default', 'var(--text-secondary)');
      document.documentElement.style.setProperty('--semantic-sidebar-link-hover', 'var(--text-primary)');
      document.documentElement.style.setProperty('--semantic-sidebar-link-active', 'var(--text-primary)');
      document.documentElement.style.setProperty('--semantic-sidebar-link-hover-bg', 'color-mix(in srgb, var(--color-brand-primary) 10%, transparent)');
    }

    // Calendar tokens
    if (isDarkTheme) {
      document.documentElement.style.setProperty('--semantic-calendar-day-bg', '#1e2730');
      document.documentElement.style.setProperty('--semantic-calendar-day-border', '#2b3944');
      document.documentElement.style.setProperty('--semantic-calendar-day-text', '#f1f5f9');
      document.documentElement.style.setProperty('--semantic-calendar-day-muted-text', '#7a8894');
      document.documentElement.style.setProperty('--semantic-calendar-today-ring', `0 0 0 2px ${primaryBase}, 0 0 0 4px color-mix(in srgb, ${primaryBase} 35%, transparent)`);
      document.documentElement.style.setProperty('--semantic-calendar-selected-bg', primaryBase);
      document.documentElement.style.setProperty('--semantic-calendar-selected-text', unifiedPrimaryText);
      document.documentElement.style.setProperty('--semantic-calendar-range-bg', `color-mix(in srgb, ${primaryBase} 15%, transparent)`);
      document.documentElement.style.setProperty('--semantic-calendar-range-border', primaryBase);
      document.documentElement.style.setProperty('--semantic-calendar-event-academic', '#6366f1');
      document.documentElement.style.setProperty('--semantic-calendar-event-exam', '#f59e0b');
      document.documentElement.style.setProperty('--semantic-calendar-event-assignment', '#10b981');
      document.documentElement.style.setProperty('--semantic-calendar-event-reminder', '#ec4899');
    } else {
      document.documentElement.style.setProperty('--semantic-calendar-day-bg', '#ffffff');
      document.documentElement.style.setProperty('--semantic-calendar-day-border', '#e2e8f0');
      document.documentElement.style.setProperty('--semantic-calendar-day-text', '#1e293b');
      document.documentElement.style.setProperty('--semantic-calendar-day-muted-text', '#64748b');
      document.documentElement.style.setProperty('--semantic-calendar-today-ring', `0 0 0 2px ${primaryBase}`);
      document.documentElement.style.setProperty('--semantic-calendar-selected-bg', primaryBase);
      document.documentElement.style.setProperty('--semantic-calendar-selected-text', unifiedPrimaryText);
      document.documentElement.style.setProperty('--semantic-calendar-range-bg', `color-mix(in srgb, ${primaryBase} 8%, transparent)`);
      document.documentElement.style.setProperty('--semantic-calendar-range-border', `color-mix(in srgb, ${primaryBase} 40%, transparent)`);
      document.documentElement.style.setProperty('--semantic-calendar-event-academic', '#6366f1');
      document.documentElement.style.setProperty('--semantic-calendar-event-exam', '#d97706');
      document.documentElement.style.setProperty('--semantic-calendar-event-assignment', '#059669');
      document.documentElement.style.setProperty('--semantic-calendar-event-reminder', '#db2777');
    }

    // Intent / status tokens (backgrounds subtle, text legible)
    if (isDarkTheme) {
      
      // Warmer, more muted intent colors that complement the gold accent
      document.documentElement.style.setProperty('--intent-success-bg', 'color-mix(in srgb, #22c55e 10%, #161b22)');
      document.documentElement.style.setProperty('--intent-success-border', 'color-mix(in srgb, #22c55e 25%, transparent)');
      document.documentElement.style.setProperty('--intent-success-text', '#6ee7b7');
      document.documentElement.style.setProperty('--intent-warning-bg', 'color-mix(in srgb, #f59e0b 10%, #161b22)');
      document.documentElement.style.setProperty('--intent-warning-border', 'color-mix(in srgb, #fbbf24 25%, transparent)');
      document.documentElement.style.setProperty('--intent-warning-text', '#fcd34d');
      document.documentElement.style.setProperty('--intent-danger-bg', 'color-mix(in srgb, #dc2626 10%, #161b22)');
      document.documentElement.style.setProperty('--intent-danger-border', 'color-mix(in srgb, #f87171 25%, transparent)');
      document.documentElement.style.setProperty('--intent-danger-text', '#fca5a5');
      document.documentElement.style.setProperty('--intent-info-bg', 'color-mix(in srgb, #3b82f6 10%, #161b22)');
      document.documentElement.style.setProperty('--intent-info-border', 'color-mix(in srgb, #60a5fa 25%, transparent)');
      document.documentElement.style.setProperty('--intent-info-text', '#93c5fd');
    } else {
      document.documentElement.style.setProperty('--intent-success-bg', '#ecfdf5');
      document.documentElement.style.setProperty('--intent-success-border', '#a7f3d0');
      document.documentElement.style.setProperty('--intent-success-text', '#065f46');
      document.documentElement.style.setProperty('--intent-warning-bg', '#fffbeb');
      document.documentElement.style.setProperty('--intent-warning-border', '#fde68a');
      document.documentElement.style.setProperty('--intent-warning-text', '#92400e');
      document.documentElement.style.setProperty('--intent-danger-bg', '#fef2f2');
      document.documentElement.style.setProperty('--intent-danger-border', '#fecaca');
      document.documentElement.style.setProperty('--intent-danger-text', '#991b1b');
      document.documentElement.style.setProperty('--intent-info-bg', '#eff6ff');
      document.documentElement.style.setProperty('--intent-info-border', '#bfdbfe');
      document.documentElement.style.setProperty('--intent-info-text', '#1e3a8a');
      document.documentElement.style.setProperty('--intent-neutral-bg', '#f1f5f9');
      document.documentElement.style.setProperty('--intent-neutral-border', '#cbd5e1');
      document.documentElement.style.setProperty('--intent-neutral-text', '#475569');
      
      // Scholar Mode-specific intent colors with warm, muted academic palette
      if (currentTheme === 'scholarMode') {
        document.documentElement.style.setProperty('--intent-success-bg', '#eef9e8'); // soft sage green
        document.documentElement.style.setProperty('--intent-success-border', '#b8dba4');
        document.documentElement.style.setProperty('--intent-success-text', '#2d5016');
        document.documentElement.style.setProperty('--intent-warning-bg', '#fff7e6'); // warm amber
        document.documentElement.style.setProperty('--intent-warning-border', '#f5d399');
        document.documentElement.style.setProperty('--intent-warning-text', '#7c5d10');
        document.documentElement.style.setProperty('--intent-danger-bg', '#fceee8'); // soft terracotta
        document.documentElement.style.setProperty('--intent-danger-border', '#ebb89e');
        document.documentElement.style.setProperty('--intent-danger-text', '#7a2e0e');
        document.documentElement.style.setProperty('--intent-info-bg', '#e8f0f9'); // soft slate blue
        document.documentElement.style.setProperty('--intent-info-border', '#a8c5e0');
        document.documentElement.style.setProperty('--intent-info-text', '#1d3557');
      }
    }

    // Badge tokens (may mirror intent but can diverge later)
    const badgeMap = [
      ['accent','var(--color-brand-accent)','var(--color-brand-accent)'],
      ['success','var(--intent-success-text)','var(--intent-success-text)'],
      ['warning','var(--intent-warning-text)','var(--intent-warning-text)'],
      ['danger','var(--intent-danger-text)','var(--intent-danger-text)'],
      ['info','var(--intent-info-text)','var(--intent-info-text)'],
      ['neutral','var(--intent-neutral-text)','var(--intent-neutral-text)']
    ];
    badgeMap.forEach(([name, colorRef]) => {
      // Subtle translucent background depending on light/dark
      const bg = isDarkTheme ? `color-mix(in srgb, ${colorRef} 25%, transparent)` : `color-mix(in srgb, ${colorRef} 15%, transparent)`;
      const border = isDarkTheme ? `color-mix(in srgb, ${colorRef} 55%, transparent)` : `color-mix(in srgb, ${colorRef} 35%, transparent)`;
      document.documentElement.style.setProperty(`--badge-${name}-bg`, bg);
      document.documentElement.style.setProperty(`--badge-${name}-text`, colorRef);
      document.documentElement.style.setProperty(`--badge-${name}-border`, border);
    });

    // Modal tokens
    if (isDarkTheme) {
      document.documentElement.style.setProperty('--modal-bg', 'rgba(30,41,59,0.95)');
      document.documentElement.style.setProperty('--modal-border', 'rgba(255,255,255,0.12)');
      document.documentElement.style.setProperty('--modal-shadow', '0 8px 32px -8px rgba(0,0,0,0.55)');
      document.documentElement.style.setProperty('--modal-backdrop', 'rgba(0,0,0,0.55)');
    } else {
      document.documentElement.style.setProperty('--modal-bg', '#ffffff');
      document.documentElement.style.setProperty('--modal-border', 'rgba(0,0,0,0.08)');
      document.documentElement.style.setProperty('--modal-shadow', '0 8px 32px -8px rgba(0,0,0,0.12)');
      document.documentElement.style.setProperty('--modal-backdrop', 'rgba(15,23,42,0.35)');
    }

    // Scrollbar tokens (used across lists & panels)
    if (isDarkTheme) {
      document.documentElement.style.setProperty('--scrollbar-track-bg', '#1e293b');
      document.documentElement.style.setProperty('--scrollbar-thumb-bg', '#475569');
      document.documentElement.style.setProperty('--scrollbar-thumb-hover-bg', '#64748b');
    } else {
      document.documentElement.style.setProperty('--scrollbar-track-bg', '#f1f5f9');
      document.documentElement.style.setProperty('--scrollbar-thumb-bg', '#cbd5e1');
      document.documentElement.style.setProperty('--scrollbar-thumb-hover-bg', '#94a3b8');
    }

    // Table / list tokens (new) – unify document list, generic data tables & list highlight
    if (isDarkTheme) {
      document.documentElement.style.setProperty('--table-bg', '#161d25');
      document.documentElement.style.setProperty('--table-border', '#2b3944');
      document.documentElement.style.setProperty('--table-header-bg', '#1e2730');
      document.documentElement.style.setProperty('--table-header-text', '#f1f5f9');
  document.documentElement.style.setProperty('--table-row-bg', '#181f27');
  document.documentElement.style.setProperty('--table-row-alt-bg', '#1c242d');
  document.documentElement.style.setProperty('--table-row-hover-bg', '#22303b');
  document.documentElement.style.setProperty('--table-row-active-bg', '#2d3d4a');
      document.documentElement.style.setProperty('--table-row-text', '#f1f5f9');
      document.documentElement.style.setProperty('--table-highlight-accent', 'var(--color-brand-accent)');
      document.documentElement.style.setProperty('--list-item-hover-ring', '0 0 0 1px rgba(255,255,255,0.10),0 2px 6px -2px rgba(0,0,0,0.45)');
      
      // DocuHounds-specific table colors for better integration with gold theme
      if (currentTheme === 'docuHounds') {
        document.documentElement.style.setProperty('--table-bg', '#0d0f13');
        document.documentElement.style.setProperty('--table-border', '#21262d');
        document.documentElement.style.setProperty('--table-header-bg', '#0d0f13');
        document.documentElement.style.setProperty('--table-header-text', '#d4a574'); // warm gold
        document.documentElement.style.setProperty('--table-row-text', '#d4a574'); // warm gold text for rows
        // Set both the direct variables AND the semantic overrides
        document.documentElement.style.setProperty('--table-row-bg', '#0d0f13');
        document.documentElement.style.setProperty('--table-row-alt-bg', '#0d0f13');
        document.documentElement.style.setProperty('--semantic-table-row-bg-dark', '#0d0f13');
        document.documentElement.style.setProperty('--semantic-table-row-alt-bg-dark', '#0d0f13');
        // Hover/active must differ from cardBg (#161b22) so users see feedback.
        // Use a neutral cool-gray; NEVER gold-tinted.
        document.documentElement.style.setProperty('--table-row-hover-bg', '#1e2530');
        document.documentElement.style.setProperty('--semantic-table-row-hover-bg-dark', '#1e2530');
        document.documentElement.style.setProperty('--table-row-hover', '#1e2530');
        document.documentElement.style.setProperty('--table-row-active-bg', '#242c38');
        document.documentElement.style.setProperty('--semantic-table-row-active-bg-dark', '#242c38');
        document.documentElement.style.setProperty('--table-highlight-accent', '#f2c586');
        document.documentElement.style.setProperty('--list-item-hover-ring', '0 0 0 1px rgba(242,197,134,0.2),0 2px 8px -2px rgba(242,197,134,0.15)');
      }
    } else {
      document.documentElement.style.setProperty('--table-bg', '#ffffff');
      document.documentElement.style.setProperty('--table-border', '#e2e8f0');
      document.documentElement.style.setProperty('--table-header-bg', '#f8fafc');
      document.documentElement.style.setProperty('--table-header-text', '#1e293b');
      document.documentElement.style.setProperty('--table-row-bg', '#ffffff');
      document.documentElement.style.setProperty('--table-row-alt-bg', '#f1f5f9');
      document.documentElement.style.setProperty('--table-row-hover-bg', '#eef2ff');
      document.documentElement.style.setProperty('--table-row-active-bg', '#e0e7ff');
      document.documentElement.style.setProperty('--table-row-text', '#1e293b');
      document.documentElement.style.setProperty('--table-highlight-accent', 'var(--color-brand-primary)');
      document.documentElement.style.setProperty('--list-item-hover-ring', '0 0 0 1px rgba(0,0,0,0.06),0 2px 4px -2px rgba(0,0,0,0.15)');
      
      // Scholar Mode-specific table colors for warm academic aesthetic
      if (currentTheme === 'scholarMode') {
        document.documentElement.style.setProperty('--table-bg', '#FFF8E7'); // matches cardBg
        document.documentElement.style.setProperty('--table-border', '#ddd4bd');
        document.documentElement.style.setProperty('--table-header-bg', '#f0e6ce'); // warm beige header
        document.documentElement.style.setProperty('--table-header-text', '#34495e'); // deep blue
        document.documentElement.style.setProperty('--table-row-bg', '#FFF8E7');
        document.documentElement.style.setProperty('--table-row-alt-bg', '#fef5df');
        document.documentElement.style.setProperty('--table-row-hover-bg', '#f5ecd5');
        document.documentElement.style.setProperty('--table-row-active-bg', '#ede3c8');
        document.documentElement.style.setProperty('--table-row-text', '#34495e'); // deep blue text
        document.documentElement.style.setProperty('--table-highlight-accent', '#8E44AD'); // purple accent
        document.documentElement.style.setProperty('--list-item-hover-ring', '0 0 0 1px rgba(142,68,173,0.15),0 2px 4px -2px rgba(0,0,0,0.1)');
      }
    }

    // Calendar-specific tokens - used by Calendar component
    if (isDarkTheme) {
      document.documentElement.style.setProperty('--calendar-bg', 'var(--card-bg)');
      document.documentElement.style.setProperty('--calendar-day-bg', '#1e2730');
      document.documentElement.style.setProperty('--calendar-day-border', '#2b3944');
      document.documentElement.style.setProperty('--calendar-day-text', 'var(--text-primary)');
      document.documentElement.style.setProperty('--calendar-day-hover-bg', '#27323c');
      if (currentTheme === 'docuHounds') {
        document.documentElement.style.setProperty('--calendar-selected-bg', 'rgba(242, 197, 134, 0.12)');
        document.documentElement.style.setProperty('--calendar-selected-ring', '#f2c586');
      } else {
        document.documentElement.style.setProperty('--calendar-selected-bg', 'var(--color-brand-primary)');
      }
      document.documentElement.style.setProperty('--calendar-selected-text', '#ffffff');
      document.documentElement.style.setProperty('--calendar-event-bg', '#2d3a44');
      document.documentElement.style.setProperty('--calendar-event-stripe', 'var(--color-brand-accent)');
      document.documentElement.style.setProperty('--calendar-today-border', 'var(--color-brand-accent)');
    } else {
      document.documentElement.style.setProperty('--calendar-bg', 'var(--card-bg)');
      document.documentElement.style.setProperty('--calendar-day-bg', '#ffffff');
      document.documentElement.style.setProperty('--calendar-day-border', '#e2e8f0');
      document.documentElement.style.setProperty('--calendar-day-text', 'var(--text-primary)');
      document.documentElement.style.setProperty('--calendar-day-hover-bg', '#f8fafc');
      document.documentElement.style.setProperty('--calendar-selected-bg', 'var(--color-brand-primary)');
      document.documentElement.style.setProperty('--calendar-selected-text', '#ffffff');
      document.documentElement.style.setProperty('--calendar-event-bg', '#f1f5f9');
      document.documentElement.style.setProperty('--calendar-event-stripe', 'var(--color-brand-primary)');
      document.documentElement.style.setProperty('--calendar-today-border', 'var(--color-brand-primary)');
    }

    // Search field specialized tokens (use explicit values to avoid semantic token coupling)
    if (isDarkTheme) {
      document.documentElement.style.setProperty('--search-bg', '#222c35');
      document.documentElement.style.setProperty('--search-border', '#37424c');
      document.documentElement.style.setProperty('--search-text', '#f1f5f9');
      document.documentElement.style.setProperty('--search-placeholder', 'rgba(255,255,255,0.35)');
    } else {
      document.documentElement.style.setProperty('--search-bg', '#ffffff');
      document.documentElement.style.setProperty('--search-border', '#d9e1e7');
      document.documentElement.style.setProperty('--search-text', '#1e293b');
      document.documentElement.style.setProperty('--search-placeholder', 'rgba(0,0,0,0.55)');
    }

  // Gradient tokens (centralized) – used by legacy page styles now being tokenized
  // Primary gradients adapt to theme primary/accent; others are intent/static for now
  const primaryGradA = theme.colors.primary;
  const primaryGradB = theme.colors.accent || theme.colors.primary;
  document.documentElement.style.setProperty('--gradient-primary', `linear-gradient(135deg, ${primaryGradA}, ${primaryGradB})`);
  document.documentElement.style.setProperty('--gradient-primary-hover', `linear-gradient(135deg, ${primaryGradB}, ${primaryGradA})`);
  document.documentElement.style.setProperty('--gradient-primary-horizontal', `linear-gradient(90deg, ${primaryGradA}, ${primaryGradB})`);
  // Intent gradients (static palette set – align with Tailwind-esque scale used elsewhere)
  document.documentElement.style.setProperty('--gradient-success', 'linear-gradient(135deg,#10b981,#059669)');
  document.documentElement.style.setProperty('--gradient-success-hover', 'linear-gradient(135deg,#059669,#047857)');
  document.documentElement.style.setProperty('--gradient-warning', 'linear-gradient(135deg,#f59e0b,#d97706)');
  document.documentElement.style.setProperty('--gradient-warning-hover', 'linear-gradient(135deg,#d97706,#b45309)');
  document.documentElement.style.setProperty('--gradient-danger', 'linear-gradient(135deg,#ef4444,#dc2626)');
  document.documentElement.style.setProperty('--gradient-danger-hover', 'linear-gradient(135deg,#dc2626,#b91c1c)');
  // Soft / pastel gradients (used for completed / pending badges & info panels)
  document.documentElement.style.setProperty('--gradient-success-soft', 'linear-gradient(135deg,#dcfce7,#bbf7d0)');
  document.documentElement.style.setProperty('--gradient-info-soft', 'linear-gradient(135deg,#f0f9ff,#e0f2fe)');
  document.documentElement.style.setProperty('--gradient-info-mid', 'linear-gradient(135deg,#3b82f6,#2563eb)');
  document.documentElement.style.setProperty('--gradient-warning-soft', 'linear-gradient(135deg,#fef3c7,#fde68a)');
  document.documentElement.style.setProperty('--gradient-neutral-soft', 'linear-gradient(135deg,#f8fafc,#e2e8f0)');
  document.documentElement.style.setProperty('--gradient-disabled', 'linear-gradient(135deg,#9ca3af,#6b7280)');
  document.documentElement.style.setProperty('--gradient-danger-horizontal', 'linear-gradient(90deg,#ef4444,#dc2626)');

    // Button semantic tokens (new unified system)
    // Base sizing & radii
    document.documentElement.style.setProperty('--btn-font-weight', '600');
    document.documentElement.style.setProperty('--btn-height', '2.4rem');
    document.documentElement.style.setProperty('--btn-padding-x', '1.1rem');
    document.documentElement.style.setProperty('--btn-padding-y', '0');
    document.documentElement.style.setProperty('--btn-gap', '0.55rem');
    document.documentElement.style.setProperty('--btn-radius', currentTheme === 'docuHounds' ? '0' : '4px');
    document.documentElement.style.setProperty('--btn-radius-sm', currentTheme === 'docuHounds' ? '0' : '3px');
    document.documentElement.style.setProperty('--btn-radius-lg', currentTheme === 'docuHounds' ? '0' : '6px');
    document.documentElement.style.setProperty('--btn-shadow', isDarkTheme ? '0 2px 4px -2px rgba(0,0,0,0.45),0 3px 6px -2px rgba(0,0,0,0.35)' : '0 2px 4px -2px rgba(0,0,0,0.12),0 3px 6px -2px rgba(0,0,0,0.10)');
    document.documentElement.style.setProperty('--btn-shadow-hover', isDarkTheme ? '0 4px 10px -3px rgba(0,0,0,0.55)' : '0 4px 10px -3px rgba(0,0,0,0.18)');
    document.documentElement.style.setProperty('--btn-focus-ring', '0 0 0 2px color-mix(in srgb, var(--color-brand-primary) 50%, transparent)');

    // Determine accessible text for primary based on contrast (simplified heuristic)
    const primaryText = isDarkTheme ? '#ffffff' : '#ffffff';
    const onSolidDark = '#ffffff';
    const onSubtleDark = isDarkTheme ? 'var(--color-text-secondary)' : 'var(--color-text-muted)';

    // Primary (solid brand)
    // All light themes use the brand primary color for primary buttons.
    // The old primaryTheme white-elevated approach made buttons invisible on white pages.
    document.documentElement.style.setProperty('--btn-primary-bg', 'var(--color-brand-primary)');
    document.documentElement.style.setProperty('--btn-primary-bg-hover', 'var(--color-brand-primary-hover)');
    document.documentElement.style.setProperty('--btn-primary-bg-active', 'var(--color-brand-primary)');
    document.documentElement.style.setProperty('--btn-primary-text', primaryText);
    document.documentElement.style.setProperty('--btn-primary-border', 'transparent');

    // Accent (gold for DocuHounds else secondary accent)
    if (currentTheme === 'docuHounds') {
      // Primary buttons use gold action tokens
      document.documentElement.style.setProperty('--btn-primary-bg', 'var(--color-action-primary)');
      document.documentElement.style.setProperty('--btn-primary-bg-hover', 'var(--color-action-primary-hover)');
      document.documentElement.style.setProperty('--btn-primary-text', 'var(--color-action-primary-text)');
      document.documentElement.style.setProperty('--btn-primary-border', 'rgba(255,255,255,0.12)');
      
      // Subtle depth for premium feel
      document.documentElement.style.setProperty('--btn-shadow', '0 6px 16px rgba(0,0,0,0.35)');
      document.documentElement.style.setProperty('--btn-shadow-hover', '0 8px 20px rgba(0,0,0,0.40)');
      
      // Accent buttons (keep existing gold)
      document.documentElement.style.setProperty('--btn-accent-bg', theme.colors.accent);
      document.documentElement.style.setProperty('--btn-accent-bg-hover', '#e9bb7a');
      document.documentElement.style.setProperty('--btn-accent-bg-active', '#d9af71');
      document.documentElement.style.setProperty('--btn-accent-text', '#0d1218');
      
      // Checkbox gold checked state (override global with dark text for gold background)
      document.documentElement.style.setProperty('--checkbox-check-color', '#0d0f13');
    } else {
      document.documentElement.style.setProperty('--btn-accent-bg', 'var(--color-brand-accent)');
      document.documentElement.style.setProperty('--btn-accent-bg-hover', 'color-mix(in srgb, var(--color-brand-accent) 85%, #000)');
      document.documentElement.style.setProperty('--btn-accent-bg-active', 'var(--color-brand-accent)');
      document.documentElement.style.setProperty('--btn-accent-text', onSolidDark);
    }
    document.documentElement.style.setProperty('--btn-accent-border', 'transparent');

    // Neutral (background elevated subtle)
    const neutralBase = isDarkTheme ? '#27323c' : '#e2e8f0';
    const neutralActive = isDarkTheme ? '#3b4a56' : '#b8c2cc';
    document.documentElement.style.setProperty('--btn-neutral-bg', neutralBase);
      // const neutralHover = isDarkTheme ? '#323f4a' : '#cbd5e1';
  // Use previously derived unifiedNeutralHover (mapped from secondaryHover) for neutral button hover state
  document.documentElement.style.setProperty('--btn-neutral-bg-hover', unifiedNeutralHover);
    document.documentElement.style.setProperty('--btn-neutral-bg-active', neutralActive);
    document.documentElement.style.setProperty('--btn-neutral-text', 'var(--color-text-primary)');
    document.documentElement.style.setProperty('--btn-neutral-border', isDarkTheme ? '#3a4a55' : '#94a3b8');

    // Outline (transparent bg)
    document.documentElement.style.setProperty('--btn-outline-bg', 'transparent');
    document.documentElement.style.setProperty('--btn-outline-bg-hover', isDarkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)');
    document.documentElement.style.setProperty('--btn-outline-bg-active', isDarkTheme ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)');
  document.documentElement.style.setProperty('--btn-outline-text', 'var(--color-text-primary)');
  document.documentElement.style.setProperty('--btn-outline-border', isPrimaryTheme ? '#94a3b8' : (isDarkTheme ? '#3a4a55' : '#94a3b8'));

    // Ghost (minimal – text only)
    document.documentElement.style.setProperty('--btn-ghost-bg', 'transparent');
    document.documentElement.style.setProperty('--btn-ghost-bg-hover', isDarkTheme ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)');
    document.documentElement.style.setProperty('--btn-ghost-bg-active', isDarkTheme ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)');
    document.documentElement.style.setProperty('--btn-ghost-text', 'var(--color-text-secondary)');
    document.documentElement.style.setProperty('--btn-ghost-text-hover', 'var(--color-text-primary)');
    document.documentElement.style.setProperty('--btn-ghost-border', 'transparent');

    // Danger (intent)
    document.documentElement.style.setProperty('--btn-danger-bg', isDarkTheme ? '#dc2626' : '#dc2626');
    document.documentElement.style.setProperty('--btn-danger-bg-hover', isDarkTheme ? '#ef4444' : '#b91c1c');
    document.documentElement.style.setProperty('--btn-danger-bg-active', isDarkTheme ? '#b91c1c' : '#991b1b');
    document.documentElement.style.setProperty('--btn-danger-text', '#ffffff');
    document.documentElement.style.setProperty('--btn-danger-border', 'transparent');

    // Subtle (background tint, low emphasis)
    const subtleBg = isDarkTheme ? '#1e2730' : '#f1f5f9';
    const subtleHover = isDarkTheme ? '#27323c' : '#e2e8f0';
    const subtleActive = isDarkTheme ? '#2f3b45' : '#cbd5e1';
    document.documentElement.style.setProperty('--btn-subtle-bg', subtleBg);
    document.documentElement.style.setProperty('--btn-subtle-bg-hover', subtleHover);
    document.documentElement.style.setProperty('--btn-subtle-bg-active', subtleActive);
    document.documentElement.style.setProperty('--btn-subtle-text', onSubtleDark);
    document.documentElement.style.setProperty('--btn-subtle-border', 'transparent');

    // Disabled state tokens
    document.documentElement.style.setProperty('--btn-disabled-bg', isDarkTheme ? '#2a343d' : '#e5e7eb');
    document.documentElement.style.setProperty('--btn-disabled-text', isDarkTheme ? '#6b7280' : '#94a3b8');
    document.documentElement.style.setProperty('--btn-disabled-border', 'transparent');

    // Scholar Mode-specific button refinements for academic aesthetic
    if (currentTheme === 'scholarMode') {
      // Primary: Deep scholarly blue
      document.documentElement.style.setProperty('--btn-primary-bg', '#34495e');
      document.documentElement.style.setProperty('--btn-primary-bg-hover', '#2c3e50');
      document.documentElement.style.setProperty('--btn-primary-bg-active', '#243342');
      document.documentElement.style.setProperty('--btn-primary-text', '#F7F2E3');
      // Accent: Purple for scholarly emphasis
      document.documentElement.style.setProperty('--btn-accent-bg', '#8E44AD');
      document.documentElement.style.setProperty('--btn-accent-bg-hover', '#7d3c98');
      document.documentElement.style.setProperty('--btn-accent-bg-active', '#6c3483');
      document.documentElement.style.setProperty('--btn-accent-text', '#ffffff');
      // Neutral: Warm beige
      document.documentElement.style.setProperty('--btn-neutral-bg', '#f0e6ce');
      document.documentElement.style.setProperty('--btn-neutral-bg-hover', '#e8dcc0');
      document.documentElement.style.setProperty('--btn-neutral-bg-active', '#dfd2b3');
      document.documentElement.style.setProperty('--btn-neutral-text', '#34495e');
    }

    // Legacy alias bridging (normalize expected variable names across older CSS files)
    const alias = {
      '--card-bg': '--semantic-surface-card',
      '--panel-bg': '--semantic-surface-panel',
      '--section-bg': '--semantic-surface-section',
      '--app-bg': '--semantic-surface-app',
      '--border-default': '--color-border-default',
      '--border-strong': '--color-border-strong',
      '--text-primary': '--color-text-primary',
      '--text-secondary': '--color-text-secondary',
      '--text-muted': '--color-text-muted',
      '--text-inverse': '--color-text-inverse',
      // intentionally DO NOT alias action-primary tokens here; they are explicitly set earlier per theme
      '--input-bg': '--semantic-input-bg',
      '--input-border': '--semantic-input-border',
      '--input-text': '--semantic-input-text',
      '--table-header-bg': '--table-header-bg',
      '--table-row-hover-bg': '--table-row-hover-bg',
      '--table-row-alt-bg': '--table-row-alt-bg',
      '--table-row-bg': '--table-row-bg',
      '--search-bg': '--search-bg',
      '--search-border': '--search-border',
      '--search-text': '--search-text',
      '--nav-link': '--semantic-sidebar-link-default',
      '--nav-link-hover': '--semantic-sidebar-link-hover',
      '--nav-link-active': '--semantic-sidebar-link-active',
      '--nav-link-hover-bg': '--semantic-sidebar-link-hover-bg',
      '--sidebar-bg': '--semantic-sidebar-bg',
      '--sidebar-border': '--semantic-sidebar-border'
    };
    Object.entries(alias).forEach(([legacy, modern]) => {
      const val = document.documentElement.style.getPropertyValue(modern);
      if (val) document.documentElement.style.setProperty(legacy, val);
    });

    // Alias legacy calendar variable names consumed by existing CSS (transition period)
    const aliasPairs = [
      ['--calendar-day-bg','--semantic-calendar-day-bg'],
      ['--calendar-day-border','--semantic-calendar-day-border'],
      ['--calendar-day-text','--semantic-calendar-day-text'],
      ['--calendar-day-muted-text','--semantic-calendar-day-muted-text'],
      ['--calendar-today-ring','--semantic-calendar-today-ring'],
      ['--calendar-selected-bg','--semantic-calendar-selected-bg'],
      ['--calendar-selected-text','--semantic-calendar-selected-text'],
      ['--calendar-range-bg','--semantic-calendar-range-bg'],
      ['--calendar-range-border','--semantic-calendar-range-border'],
      ['--calendar-event-academic','--semantic-calendar-event-academic'],
      ['--calendar-event-exam','--semantic-calendar-event-exam'],
      ['--calendar-event-assignment','--semantic-calendar-event-assignment'],
      ['--calendar-event-reminder','--semantic-calendar-event-reminder']
    ];
    aliasPairs.forEach(([legacy, modern]) => {
      const val = document.documentElement.style.getPropertyValue(modern);
      if (val) document.documentElement.style.setProperty(legacy, val.trim());
    });

    // Add theme-specific class (append, don't overwrite other classes)
    const body = document.body;
    // Remove any previous theme-* class
    body.classList.forEach(cls => { if (cls.startsWith('theme-')) body.classList.remove(cls); });
    body.classList.add(`theme-${currentTheme}`);

    // Map theme to light/dark token set for semantic variables
    const DARK_THEMES = new Set(['darkLab', 'retroVibes', 'docuHounds', 'midnightGold', 'velvetNight', 'steelCore', 'neonCircuit', 'crimsonFocus']);
    const isDark = DARK_THEMES.has(currentTheme);
    document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
  }, [currentTheme]);
  
  const toggleAudio = (audioType) => {
    if (!audioEnabled || currentAudio !== audioType) {
      setAudioEnabled(true);
      setCurrentAudio(audioType);
      // Logic to play audio would go here
    } else {
      setAudioEnabled(false);
      setCurrentAudio(null);
      // Logic to stop audio would go here
    }
  };
  
  return (
    <ThemeContext.Provider 
      value={{
        currentTheme,
  setTheme: setCurrentTheme,
        persistThemeImmediately: async () => {
          // Use shared PreferencesContext for persistence (no direct API call)
          try {
            // eslint-disable-next-line no-console
            if (process.env.NODE_ENV !== 'production') console.log('[Theme] Immediate persist via patchPreferences', { theme: currentTheme });
            await patchPreferences({ theme: currentTheme });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[Theme] Immediate persist failed:', e.message || e);
          }
        },
        themes,
        audioEnabled,
        currentAudio,
  toggleAudio,
  savingTheme: false
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};